import Image from 'next/image';
import { useState, useEffect } from 'react';

import { Box, Grid, Typography, Button, Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';

import EnrollBundleButton from '../enrollBundleButton';
import { ProductDetailContainer, PurchaseButton } from '../productDetail/style';

import { FooterCpdDetail } from './style';

import NextLink from 'src/components/atoms/link';
import { EnrollmentStatus } from 'src/helpers/constants';
import { numberWithCommas } from 'src/helpers/utils';
import { CPDEnrollmentStatusEnum } from 'src/interfaces';
import { ICPDCourseEnrollment } from 'src/interfaces/course';
import { IProductDetail } from 'src/interfaces/product';

type Props = {
  productDetail: IProductDetail;
  isLoading: boolean;
  height: number;
  onChooseEnrollClick: () => void;
  cpdEnrollmentStatus: EnrollmentStatus;
  cpdCourseEnrollment?: ICPDCourseEnrollment;
  courseAmount?: number;
};

const Footer = ({
  productDetail,
  isLoading,
  height,
  onChooseEnrollClick,
  cpdEnrollmentStatus,
  cpdCourseEnrollment,
  courseAmount = 0,
}: Props) => {
  const [scrollY, setScrollY] = useState(0);
  const [isCheckYourCourseClick, setIsCheckYourCourseClick] = useState<boolean>(false);
  const { t } = useTranslation();

  const canEnrollToCourse = cpdEnrollmentStatus === EnrollmentStatus.AVAILABLE;

  const originalPrice = productDetail.original_price;
  const salePrice = productDetail.sale_price;

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const renderCheckYourCourseButtonLink = () => (
    <NextLink href="/">
      <PurchaseButton
        height="40px"
        fontSize="18px"
        fullWidth
        onClick={() => setIsCheckYourCourseClick(true)}
        loading={isCheckYourCourseClick}
      >
        {t('cpd_course.check_your_course')}
      </PurchaseButton>
    </NextLink>
  );

  return (
    <FooterCpdDetail isSticky={scrollY - 50 > height}>
      <ProductDetailContainer padding="1rem">
        <Box>
          <Grid container sx={{ height: '100%', display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <Grid xs={4} item>
              {!originalPrice || originalPrice === salePrice || originalPrice < salePrice ? (
                <Typography component="div" variant="h6_deprecated">
                  {numberWithCommas(originalPrice!)} {t('home.course_card_baht')}
                </Typography>
              ) : (
                <>
                  <Typography component="div" variant="h3" lineHeight="0.7" sx={{ mt: 1 }} color="white">
                    {numberWithCommas(salePrice)} {t('home.course_card_baht')}
                  </Typography>
                  <Typography component="div" className="strike-center text-discount" variant="h6_deprecated">
                    {numberWithCommas(originalPrice)} {t('home.course_card_baht')}
                  </Typography>
                </>
              )}
            </Grid>
            <Grid xs={8} item>
              <Box>
                <EnrollBundleButton
                  style={{ fontSize: '18px' }}
                  isFooter
                  isLoading={isLoading}
                  type={cpdEnrollmentStatus}
                  onClick={onChooseEnrollClick}
                />
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Box
              sx={{
                display: { lg: 'block', xs: 'none' },
                position: 'relative',
                width: '176px',
                height: '99px',
                img: { borderRadius: '10px' },
              }}
            >
              <Image src={productDetail.footer_card_image!} alt={productDetail.footer_card_image} layout="fill" />
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ width: { lg: '85%', xs: '100%' } }}>
              <Box sx={{ ml: { lg: '36px', sm: 0 }, width: { md: '90%', lg: '60%' } }}>
                <Typography variant="h7" className="text-discount">
                  {t('product_detail.bundle.typeOverline.courses', {
                    courseAmount,
                  })}
                </Typography>
                <Typography
                  sx={{ typography: { md: 'h4', xs: 'h7' }, lineHeight: 1 }}
                  color="white"
                  className="ellipsis-2"
                >
                  {productDetail.name}
                </Typography>
              </Box>
              <Box sx={{ width: { lg: '34%', md: '70%' } }}>
                {!originalPrice || originalPrice === salePrice || originalPrice < salePrice ? (
                  <Typography
                    component="span"
                    sx={{ mb: 2, typography: { md: 'h1', xs: 'h3' }, lineHeight: 1 }}
                    color="white"
                  >
                    {numberWithCommas(salePrice)} {t('home.course_card_baht')}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      component="span"
                      sx={{ mb: 2, typography: { md: 'h1', xs: 'h3' }, lineHeight: 1 }}
                      color="white"
                    >
                      {numberWithCommas(salePrice)} {t('home.course_card_baht')}
                    </Typography>
                    <Typography
                      className="strike-center text-discount"
                      sx={{
                        mx: '1rem',
                        typography: { md: 'h4', xs: 'h7' },
                        position: 'relative',
                        bottom: 2,
                      }}
                      component="span"
                    >
                      {numberWithCommas(originalPrice)} {t('home.course_card_baht')}
                    </Typography>
                  </>
                )}
                <EnrollBundleButton
                  style={{ fontSize: '18px' }}
                  isFooter
                  isLoading={isLoading}
                  type={cpdEnrollmentStatus}
                  onClick={onChooseEnrollClick}
                />
              </Box>
            </Stack>
          </Box>
        </Box>
      </ProductDetailContainer>
    </FooterCpdDetail>
  );
};

export default Footer;
